/**
 * EntryModelCategorySection Component
 *
 * SPECIFIC section for managing Entry Model Categories.
 * Self-contained with own state management, data loading, and sync subscriptions.
 *
 * Features:
 * - Full CRUD: create, edit, archive, restore
 * - List display with color swatches and "used in" counts
 * - Archive toggle (only shown if archived categories exist)
 * - Cross-tab sync subscription
 * - Loading, error, and empty states
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Eye, EyeOff, AlertTriangle, RefreshCw } from 'lucide-react';

import {
  Button,
  Card,
  CardHeader,
  CardBody,
  EmptyState,
  useDisclosure,
} from '@enfomentis/ui';
import { entryModelCategoryService } from '@/services/EntryModelCategoryService';
import { entryModelService } from '@/services/EntryModelService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';
import type { EntryModel } from '@/types/schema/EntryModel';

import { CategoryListItem } from './CategoryListItem';
import { EntryModelUsedInPopover } from './EntryModelUsedInPopover';
import { EntryModelCategoryModal } from './EntryModelCategoryModal';
import { ArchiveCategoryWarningModal } from './ArchiveCategoryWarningModal';

export const EntryModelCategorySection: React.FC = () => {
  // Data state
  const [categories, setCategories] = useState<EntryModelCategory[]>([]);
  const [entryModels, setEntryModels] = useState<EntryModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // UI state
  const [showArchived, setShowArchived] = useState(false);

  // Modal state
  const modalDisclosure = useDisclosure();
  const [editingCategory, setEditingCategory] = useState<EntryModelCategory | undefined>();
  const [archivingCategory, setArchivingCategory] = useState<EntryModelCategory | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  // Load all data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const [allCategories, allEntryModels] = await Promise.all([
        entryModelCategoryService.getAll(),
        entryModelService.getAll(),
      ]);
      setCategories(allCategories);
      setEntryModels(allEntryModels);
    } catch (error) {
      console.error('Failed to load entry model category data:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Subscribe to cross-tab sync for category changes
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('entry_model_categories', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Subscribe to entry model changes (for "used in" count updates)
  useEffect(() => {
    const unsubscribe = syncCoordinatorService.subscribe('entry_models', () => {
      loadData();
    });
    return () => unsubscribe();
  }, [loadData]);

  // Build usedInMap: categoryId -> entryModelNames[]
  const usedInMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const entryModel of entryModels) {
      if (entryModel.entryModelCategoryId) {
        const names = map.get(entryModel.entryModelCategoryId) || [];
        names.push(entryModel.name);
        map.set(entryModel.entryModelCategoryId, names);
      }
    }
    return map;
  }, [entryModels]);

  // Check if there are any archived categories
  const hasArchivedCategories = useMemo(() => {
    return categories.some(c => c.status === 'archived');
  }, [categories]);

  // Filter and sort categories
  const sortedCategories = useMemo(() => {
    // Step 1: Apply archived filter
    const filtered = showArchived
      ? categories
      : categories.filter(c => c.status !== 'archived');

    // Step 2: Sort - active first (alphabetical), then archived (alphabetical)
    return [...filtered].sort((a, b) => {
      // Active items always first
      if (a.status === 'archived' && b.status !== 'archived') return 1;
      if (a.status !== 'archived' && b.status === 'archived') return -1;

      // Then alphabetical by name (case-insensitive)
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }, [categories, showArchived]);

  // Handlers
  const handleAddClick = () => {
    setEditingCategory(undefined);
    modalDisclosure.onOpen();
  };

  const handleEditClick = (category: EntryModelCategory) => {
    setEditingCategory(category);
    modalDisclosure.onOpen();
  };

  const handleSave = async (data: { name: string; description?: string; color: string }) => {
    setIsSaving(true);
    try {
      if (editingCategory) {
        // Update existing category
        await entryModelCategoryService.update(editingCategory.id, data);
        syncCoordinatorService.notifyChange('entry_model_categories', editingCategory.id);
      } else {
        // Create new category with next order value
        // Use fallback of 0 for any undefined order values to prevent NaN
        const nextOrder = categories.length > 0
          ? Math.max(...categories.map(c => c.order ?? 0)) + 1
          : 0;
        const newCategory = await entryModelCategoryService.add({
          ...data,
          order: nextOrder,
        });
        syncCoordinatorService.notifyChange('entry_model_categories', newCategory.id);
      }
      await loadData();
      modalDisclosure.onClose();
    } catch (error) {
      console.error('Failed to save entry model category:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchiveClick = (category: EntryModelCategory) => {
    setArchivingCategory(category);
  };

  const handleArchiveConfirm = async () => {
    if (!archivingCategory) return;
    try {
      await entryModelCategoryService.update(archivingCategory.id, { status: 'archived' });
      syncCoordinatorService.notifyChange('entry_model_categories', archivingCategory.id);
      await loadData();
    } catch (error) {
      console.error('Failed to archive entry model category:', error);
    } finally {
      setArchivingCategory(undefined);
    }
  };

  const handleArchiveCancel = () => {
    setArchivingCategory(undefined);
  };

  const handleRestore = async (category: EntryModelCategory) => {
    try {
      await entryModelCategoryService.update(category.id, { status: 'active' });
      syncCoordinatorService.notifyChange('entry_model_categories', category.id);
      await loadData();
    } catch (error) {
      console.error('Failed to restore entry model category:', error);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card radius="none" shadow="none">
        <CardHeader className="py-3 border-b border-default-200 bg-default-50">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-text-primary">Entry Model Categories</span>
            <p className="text-xs text-text-secondary truncate" title="Optional groupings to organize entry models">
              Optional groupings to organize entry models
            </p>
          </div>
        </CardHeader>
        <CardBody className="px-4 py-4">
          <div className="text-center py-8">
            <p className="text-text-secondary">Loading categories...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Card radius="none" shadow="none">
        <CardHeader className="py-3 border-b border-default-200 bg-default-50">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-text-primary">Entry Model Categories</span>
            <p className="text-xs text-text-secondary truncate" title="Optional groupings to organize entry models">
              Optional groupings to organize entry models
            </p>
          </div>
        </CardHeader>
        <CardBody className="px-4 py-4">
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <div className="flex items-center gap-2 text-danger-600">
              <AlertTriangle size={20} />
              <p className="text-sm">Failed to load categories</p>
            </div>
            <Button
              size="sm"
              variant="flat"
              startContent={<RefreshCw size={16} />}
              onPress={loadData}
            >
              Retry
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Empty state
  if (categories.length === 0) {
    return (
      <>
        <Card radius="none" shadow="none">
          <CardHeader className="py-3 border-b border-default-200 bg-default-50">
            <div className="flex flex-col gap-0.5">
              <span className="font-semibold text-text-primary">Entry Model Categories</span>
              <p className="text-xs text-text-secondary truncate" title="Optional groupings to organize entry models">
                Optional groupings to organize entry models
              </p>
            </div>
          </CardHeader>
          <CardBody className="px-4 py-4">
            <EmptyState
              title="No categories yet"
              description="Entry model categories are optional groupings to organize your entry models. Create categories to add structure to your trading setups."
              action={
                <Button
                  variant="light"
                  startContent={<Plus size={16} />}
                  onPress={handleAddClick}
                >
                  Add Category
                </Button>
              }
            />
          </CardBody>
        </Card>
        <EntryModelCategoryModal
          isOpen={modalDisclosure.isOpen}
          onClose={modalDisclosure.onClose}
          category={editingCategory}
          onSave={handleSave}
          isSaving={isSaving}
        />
      </>
    );
  }

  // Main content
  return (
    <>
      <Card radius="none" shadow="none">
        {/* Header with controls */}
        <CardHeader className="py-3 border-b border-default-200 bg-default-50">
          <div className="flex items-center justify-between w-full gap-3">
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <span className="font-semibold text-text-primary">Entry Model Categories</span>
              <p className="text-xs text-text-secondary truncate" title="Optional groupings to organize entry models">
                Optional groupings to organize entry models
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Archived Toggle - only show if archived exist */}
              {hasArchivedCategories && (
                <Button
                  size="sm"
                  variant="flat"
                  startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                  onPress={() => setShowArchived(!showArchived)}
                  className={`bg-default-100 shrink-0 ${
                    showArchived ? 'text-text-primary' : 'text-text-tertiary'
                  }`}
                >
                  Archived
                </Button>
              )}

              {/* Add Button */}
              <Button
                variant="light"
                size="sm"
                startContent={<Plus size={16} />}
                onPress={handleAddClick}
                className="shrink-0"
              >
                Add
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardBody className="px-0 py-4">
          {/* Category List */}
          {sortedCategories.length > 0 && (
            <div className="space-y-2">
              {sortedCategories.map((category) => (
                <Card
                  key={category.id}
                  radius="sm"
                  shadow="none"
                  className="border border-default-200"
                >
                  <CardBody className="p-0">
                    <CategoryListItem
                      category={category}
                      usedInContent={
                        <EntryModelUsedInPopover
                          entryModelNames={usedInMap.get(category.id) || []}
                        />
                      }
                      onEdit={() => handleEditClick(category)}
                      onArchiveClick={() => handleArchiveClick(category)}
                      onRestore={() => handleRestore(category)}
                    />
                  </CardBody>
                </Card>
              ))}
            </div>
          )}

          {/* No results from archived filter */}
          {sortedCategories.length === 0 && categories.length > 0 && (
            <EmptyState
              title="No active categories"
              description="All categories are archived. Enable 'Archived' to view them."
            />
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Modal */}
      <EntryModelCategoryModal
        isOpen={modalDisclosure.isOpen}
        onClose={modalDisclosure.onClose}
        category={editingCategory}
        onSave={handleSave}
        isSaving={isSaving}
      />

      {/* Archive Warning Modal */}
      <ArchiveCategoryWarningModal
        isOpen={!!archivingCategory}
        category={archivingCategory}
        usageGroups={[
          {
            label: 'Entry models using this category:',
            singularLabel: 'entry model',
            pluralLabel: 'entry models',
            names: archivingCategory ? usedInMap.get(archivingCategory.id) || [] : [],
          },
        ]}
        onConfirm={handleArchiveConfirm}
        onCancel={handleArchiveCancel}
      />
    </>
  );
};
